import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './base-query';

const mcqAPI = createApi({
  reducerPath: 'mcqAPI',
  baseQuery,
  tagTypes: ['mcq', 'mcq-suggestion', 'mcq-stats', 'mcq-explanation'],
  endpoints: (builder) => ({
    getMcqs: builder.query({
      query: (params) => {
        const queryParams = {
          page: params?.page ?? 1,
          size: params?.size ?? 10,
        };

        if (params?.search && typeof params.search === 'string' && params.search.trim()) {
          queryParams.search = params.search.trim();
        }

        const rawFilter = params?.filter || params?.status;
        if (rawFilter && typeof rawFilter === 'string') {
          const trimmedFilter = rawFilter.trim();
          const upperFilter = trimmedFilter.toUpperCase();
          if (upperFilter !== 'ALL' && upperFilter !== 'EXPLANATION_PENDING') {
            queryParams.filter = trimmedFilter;
          }
        }

        return {
          url: '/api/v1/admin/mcq',
          method: 'GET',
          params: queryParams,
        };
      },
      providesTags: ['mcq'],
    }),

    getMcqById: builder.query({
      query: (mcqId) => ({
        url: `/api/v1/admin/mcq/${mcqId}`,
        method: 'GET',
      }),
      providesTags: (result, error, mcqId) => [{ type: 'mcq', id: mcqId }],
    }),

    getMcqSuggestion: builder.query({
      query: () => ({
        url: '/api/v1/admin/mcq/suggestion',
        method: 'GET',
      }),
      providesTags: ['mcq-suggestion'],
    }),

    createMcq: builder.mutation({
      query: (payload) => ({
        url: '/api/v1/admin/mcq',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['mcq', 'mcq-suggestion'],
    }),

    updateMcq: builder.mutation({
      query: ({ mcqId, id, payload, ...body }) => {
        const targetId = mcqId || id;
        return {
          url: `/api/v1/admin/mcq/${targetId}`,
          method: 'PATCH',
          body: payload || body,
        };
      },
      invalidatesTags: (result, error, arg) => {
        const id = arg?.mcqId || arg?.id;
        return [
          'mcq',
          ...(id ? [{ type: 'mcq', id }, { type: 'mcq-explanation', id }] : []),
        ];
      },
    }),
    deleteMcq: builder.mutation({
      query: (mcqId) => {
        const id = typeof mcqId === 'object' ? (mcqId?.mcqId || mcqId?.id) : mcqId;
        return {
          url: `/api/v1/admin/mcq/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => {
        const id = typeof arg === 'object' ? (arg?.mcqId || arg?.id) : arg;
        return [
          'mcq',
          'mcq-suggestion',
          ...(id ? [{ type: 'mcq', id }] : []),
        ];
      },
    }),
    updateMcqStatus: builder.mutation({
      query: ({ mcqId, id, isActive, payload }) => {
        const targetId = mcqId || id;
        const targetPayload = payload !== undefined ? payload : { isActive: Boolean(isActive) };
        return {
          url: `/api/v1/admin/mcq/${targetId}/status`,
          method: 'PATCH',
          body: targetPayload,
        };
      },
      invalidatesTags: (result, error, arg) => {
        const id = typeof arg === 'object' ? (arg?.mcqId || arg?.id) : arg;
        return [
          'mcq',
          ...(id ? [{ type: 'mcq', id }] : []),
        ];
      },
    }),
    publishMcq: builder.mutation({
      query: ({ mcqId, id, payload, publishAt, answerPublishAt }) => {
        const targetId = mcqId || id;
        const body = payload || { publishAt, answerPublishAt };
        return {
          url: `/api/v1/admin/mcq/${targetId}/publish`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (result, error, arg) => {
        const id = typeof arg === 'object' ? (arg?.mcqId || arg?.id) : arg;
        return [
          'mcq',
          ...(id ? [{ type: 'mcq', id }] : []),
        ];
      },
    }),
    getMcqStats: builder.query({
      query: (mcqId) => {
        const id = typeof mcqId === 'object' ? (mcqId?.mcqId || mcqId?.id) : mcqId;
        return {
          url: `/api/v1/admin/mcq/${id}/stats`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => {
        const id = typeof arg === 'object' ? (arg?.mcqId || arg?.id) : arg;
        return [{ type: 'mcq-stats', id }];
      },
    }),
    getMcqExplanation: builder.query({
      query: (mcqId) => {
        const id = typeof mcqId === 'object' ? (mcqId?.mcqId || mcqId?.id) : mcqId;
        return {
          url: `/api/v1/admin/explanations/${id}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => {
        const id = typeof arg === 'object' ? (arg?.mcqId || arg?.id) : arg;
        return [{ type: 'mcq-explanation', id }];
      },
    }),
    createMcqExplanation: builder.mutation({
      query: ({ mcqId, id, payload, youtubeLink, description, publishAt }) => {
        const targetId = mcqId || id;
        const body = payload || { youtubeLink, description, publishAt };
        return {
          url: `/api/v1/admin/explanations/${targetId}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (result, error, arg) => {
        const id = typeof arg === 'object' ? (arg?.mcqId || arg?.id) : arg;
        return [
          'mcq',
          ...(id ? [{ type: 'mcq', id }, { type: 'mcq-explanation', id }] : []),
        ];
      },
    }),
    updateMcqExplanation: builder.mutation({
      query: ({ mcqId, id, payload, youtubeLink, description, publishAt }) => {
        const targetId = mcqId || id;
        const body = payload || { youtubeLink, description, publishAt };
        return {
          url: `/api/v1/admin/explanations/${targetId}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: (result, error, arg) => {
        const id = typeof arg === 'object' ? (arg?.mcqId || arg?.id) : arg;
        return [
          'mcq',
          ...(id ? [{ type: 'mcq', id }, { type: 'mcq-explanation', id }] : []),
        ];
      },
    }),
  }),
});

export const {
  useGetMcqsQuery,
  useGetMcqByIdQuery,
  useGetMcqSuggestionQuery,
  useGetMcqStatsQuery,
  useGetMcqExplanationQuery,
  useCreateMcqMutation,
  useUpdateMcqMutation,
  useDeleteMcqMutation,
  useUpdateMcqStatusMutation,
  usePublishMcqMutation,
  useCreateMcqExplanationMutation,
  useUpdateMcqExplanationMutation,
} = mcqAPI;

export default mcqAPI;
